import React, { useState } from "react";
import { Button, Modal, Upload, Typography, Space, Table, Tag, message } from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import { privatePostMultipart } from "../../utils/apiHelper";

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;

const STATUS_LABELS = {
  assigned: { color: "green", label: "Assigned" },
  no_match: { color: "orange", label: "No matching contact" },
  too_large: { color: "red", label: "Too large (max 10MB)" },
};

const COLUMNS = [
  { title: "File", dataIndex: "filename", key: "filename" },
  { title: "Matched email", dataIndex: "matchedEmail", key: "matchedEmail" },
  {
    title: "Result",
    dataIndex: "status",
    key: "status",
    render: (status) => {
      const info = STATUS_LABELS[status] || { color: "default", label: status };
      return <Tag color={info.color}>{info.label}</Tag>;
    },
  },
];

export default function AttachContactFiles({ fetchRecords }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);

  const resetState = () => {
    setFiles([]);
    setUploading(false);
    setResults(null);
  };

  const showModal = () => {
    resetState();
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    if (uploading) return;
    setIsModalOpen(false);
  };

  const handleUpload = async () => {
    if (!files.length) {
      message.warning("Add at least one file.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((file) => fd.append("files", file, file.name));
      const res = await privatePostMultipart("/contacts/bulk-attachments", fd);
      setResults(res.data.result || []);
      const assignedCount = (res.data.result || []).filter(
        (r) => r.status === "assigned",
      ).length;
      if (assignedCount > 0) {
        message.success(`Assigned ${assignedCount} file(s) to contacts.`);
        fetchRecords();
      }
    } catch (e) {
      console.log(e);
      message.error("Bulk upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Button icon={<PaperClipOutlined />} onClick={showModal}>
        Attach files
      </Button>
      <Modal
        title="Attach files to contacts"
        open={isModalOpen}
        onCancel={handleCancel}
        maskClosable={false}
        footer={
          results
            ? [
                <Button key="close" type="primary" onClick={() => setIsModalOpen(false)}>
                  Done
                </Button>,
              ]
            : [
                <Button key="cancel" onClick={handleCancel} disabled={uploading}>
                  Cancel
                </Button>,
                <Button
                  key="upload"
                  type="primary"
                  loading={uploading}
                  onClick={handleUpload}
                >
                  Match &amp; Upload
                </Button>,
              ]
        }
      >
        {!results ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Paragraph>
              Each file is matched to a contact by its filename (minus the
              extension), e.g. <Text code>john@x.com.pdf</Text> is assigned to
              the contact with that email address. Files with no matching
              contact are skipped.
            </Paragraph>
            <Paragraph type="secondary" style={{ marginTop: -8 }}>
              Max 10MB per file. A file attached here is sent alongside any
              campaign that contact receives, in addition to the campaign's
              own attachments — keep the combined size in mind for email
              delivery limits (~7MB total per recipient).
            </Paragraph>
            <Dragger
              multiple
              beforeUpload={(file) => {
                setFiles((prev) => [...prev, file]);
                return false;
              }}
              fileList={files}
              onRemove={(file) =>
                setFiles((prev) => prev.filter((f) => f.uid !== file.uid))
              }
            >
              <p className="ant-upload-text">
                Click or drag files to this area to upload
              </p>
            </Dragger>
          </Space>
        ) : (
          <Table
            size="small"
            rowKey={(r, i) => `${r.filename}-${i}`}
            columns={COLUMNS}
            dataSource={results}
            pagination={false}
          />
        )}
      </Modal>
    </>
  );
}
